import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

// Notification reminder messages
const reminderMessages = [
  "Time for your daily RE-BUILD-ME-PRO check-in.",
  "A moment for your rebuild awaits.",
  "If today is hard, do the easier version.",
  "Your daily reset is ready.",
  "Take a moment to check in with yourself.",
];

// Check if running on native platform
const isNative = () => {
  const native = Capacitor.isNativePlatform();
  console.log('isNative check:', native, 'Platform:', Capacitor.getPlatform());
  return native;
};

// Check if notifications are supported
export const isNotificationSupported = () => {
  if (isNative()) {
    console.log('Notifications supported: true (native)');
    return true; // Native always supports
  }
  const supported = 'Notification' in window;
  console.log('Notifications supported:', supported, '(web)');
  return supported;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (isNative()) {
    try {
      const result = await LocalNotifications.requestPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }
  
  // Web fallback
  if (!isNotificationSupported()) {
    return false;
  }
  const permission = await Notification.requestPermission();
  return permission === 'granted';
};

// Get current notification permission
export const getNotificationPermission = async () => {
  if (isNative()) {
    try {
      const result = await LocalNotifications.checkPermissions();
      return result.display;
    } catch (error) {
      return 'denied';
    }
  }
  
  // Web fallback
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
};

// Schedule daily notification at specified time
export const scheduleNotification = async (time) => {
  if (isNative()) {
    try {
      // Cancel existing notifications first
      await cancelNotification();
      
      const [hours, minutes] = time.split(':').map(Number);
      const message = reminderMessages[Math.floor(Math.random() * reminderMessages.length)];
      
      // Schedule notification
      await LocalNotifications.schedule({
        notifications: [
          {
            id: 1,
            title: 'RE-BUILD-ME-PRO',
            body: message,
            schedule: {
              on: {
                hour: hours,
                minute: minutes,
              },
              repeats: true,
              allowWhileIdle: true,
            },
            sound: 'default',
            actionTypeId: 'OPEN_APP',
          },
        ],
      });
      
      console.log('Notification scheduled for', time);
      return true;
    } catch (error) {
      console.error('Failed to schedule notification:', error);
      return false;
    }
  }
  
  // Web fallback (simplified - only works while app is open)
  scheduleWebNotification(time);
  return true;
};

// Cancel all scheduled notifications
export const cancelNotification = async () => {
  if (isNative()) {
    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({
          notifications: pending.notifications,
        });
      }
      console.log('Notifications cancelled');
    } catch (error) {
      console.error('Failed to cancel notifications:', error);
    }
  } else {
    // Web fallback
    cancelWebNotification();
  }
};

// Web-only: Schedule notification (simplified - will work while app is open)
let notificationTimeout = null;
let oneOffWebReminderTimeouts = new Map();

const scheduleWebNotification = (time) => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return;
  }
  
  // Clear any existing scheduled notification
  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
  }
  
  const now = new Date();
  const [hours, minutes] = time.split(':').map(Number);
  
  let targetTime = new Date();
  targetTime.setHours(hours, minutes, 0, 0);
  
  // If the time has passed today, schedule for tomorrow
  if (targetTime <= now) {
    targetTime.setDate(targetTime.getDate() + 1);
  }
  
  const delay = targetTime.getTime() - now.getTime();
  
  notificationTimeout = setTimeout(() => {
    showWebNotification();
    // Re-schedule for the next day
    scheduleWebNotification(time);
  }, delay);
};

// Show web notification
const showWebNotification = () => {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return;
  }
  
  const message = reminderMessages[Math.floor(Math.random() * reminderMessages.length)];
  
  new Notification('RE-BUILD-ME-PRO', {
    body: message,
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'rebuildme-reminder',
  });
};

// Cancel web notification
const cancelWebNotification = () => {
  if (notificationTimeout) {
    clearTimeout(notificationTimeout);
    notificationTimeout = null;
  }
};

// Initialize notification listeners (call on app start)
export const initializeNotifications = async () => {
  if (isNative()) {
    try {
      // Listen for notification actions
      await LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
        console.log('Notification action:', notification);
        // Could navigate to daily log here
      });
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
    }
  }
};

const computeNextDueAt = (timeHHMM) => {
  const m = String(timeHHMM || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  const now = new Date();
  const due = new Date();
  due.setHours(hh, mm, 0, 0);
  if (due.getTime() <= now.getTime()) due.setDate(due.getDate() + 1);
  return due;
};

export const scheduleOneOffReminder = async ({ id, time, text }) => {
  const dueAt = computeNextDueAt(time);
  if (!dueAt) return { ok: false, reason: 'invalid_time', dueAtIso: '' };
  const dueAtIso = dueAt.toISOString();
  const body = String(text || '').trim().slice(0, 220) || 'Quick reminder from RE-BUILD-ME-PRO.';

  const permission = await getNotificationPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_denied', dueAtIso };
  }

  if (isNative()) {
    try {
      const nid = Number(String(id || '').replace(/\D+/g, '').slice(-7)) || Math.floor(Date.now() / 1000);
      await LocalNotifications.schedule({
        notifications: [
          {
            id: nid,
            title: 'RE-BUILD-ME-PRO reminder',
            body,
            schedule: { at: dueAt, allowWhileIdle: true },
            sound: 'default',
            actionTypeId: 'OPEN_APP',
          },
        ],
      });
      return { ok: true, reason: 'scheduled', dueAtIso };
    } catch (error) {
      console.error('Failed to schedule one-off reminder:', error);
      return { ok: false, reason: 'schedule_failed', dueAtIso };
    }
  }

  const delay = Math.max(0, dueAt.getTime() - Date.now());
  const key = String(id || `rem_${Date.now()}`);
  const prev = oneOffWebReminderTimeouts.get(key);
  if (prev) clearTimeout(prev);
  const timeoutId = setTimeout(() => {
    oneOffWebReminderTimeouts.delete(key);
    if (Notification.permission !== 'granted') return;
    new Notification('RE-BUILD-ME-PRO reminder', {
      body,
      icon: '/favicon.ico',
      tag: `rebuildme-reminder-${key}`,
    });
  }, delay);
  oneOffWebReminderTimeouts.set(key, timeoutId);
  return { ok: true, reason: 'scheduled', dueAtIso };
};
